import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useViewFormHook } from '../api/report/report';
import { TaxReturnItemRequest, ViewFormResponse } from '../model';
import { CancellablePromise } from '../utils/UseCustomInstance';

const AUTO_CANCEL_TIMEOUT_MS = 29000; // 29 seconds, before the 30s timeout from server

/**
 * Custom hook that wraps useViewForm with cancellation support
 * Returns standard React Query mutation result plus a cancel function
 * Automatically cancels requests after 30 seconds
 */
export function useCancellableViewForm<TError = unknown, TContext = unknown>(
    options?: UseMutationOptions<ViewFormResponse, TError, TaxReturnItemRequest, TContext>
) {
    const viewFormHook = useViewFormHook();
    const currentRequestRef = useRef<CancellablePromise<ViewFormResponse> | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const clearAutoCancelTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const cancel = () => {
        if (currentRequestRef.current && typeof currentRequestRef.current.cancel === 'function') {
            currentRequestRef.current.cancel();
            currentRequestRef.current = null;
        }
        clearAutoCancelTimeout();
    };

    const mutation = useMutation<ViewFormResponse, TError, TaxReturnItemRequest, TContext>({
        mutationFn: (data: TaxReturnItemRequest) => {
            // Clear any existing timeout before starting a new request
            clearAutoCancelTimeout();

            const cancellablePromise = viewFormHook(data);
            currentRequestRef.current = cancellablePromise;

            // Set up auto-cancel after 30 seconds
            timeoutRef.current = setTimeout(() => {
                if (currentRequestRef.current && typeof currentRequestRef.current.cancel === 'function') {
                    currentRequestRef.current.cancel();
                    currentRequestRef.current = null;
                }
            }, AUTO_CANCEL_TIMEOUT_MS);

            return cancellablePromise;
        },
        onSettled: () => {
            // Clear the ref and timeout when the request completes (success or error)
            currentRequestRef.current = null;
            clearAutoCancelTimeout();
        },
        ...options,
    });

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            clearAutoCancelTimeout();
        };
    }, []);

    return {
        ...mutation,
        cancel,
    };
}
